/**
 * The screen a nav entry points at until its backlog task builds it. Recomposed with D66.
 *
 * `EmptyState`'s own contract: *"The sentence must explain why it is empty, not just that it
 * is."* So each of these names what has to exist first rather than saying "coming soon" — which
 * tells the reader nothing they could not see.
 *
 * They exist at all because the alternative is a sidebar with nine dead entries. A nav item that
 * navigates nowhere is worse than one that navigates somewhere honest.
 *
 * ## 🔴 Why this file got the composition pass rather than nine screen files
 *
 * D66 rebuilt the world, the shell, S14 and S6 — and left nine screens holding the new tokens and
 * the new chrome inside the old composition: a bare `<EmptyState>` paragraph in the top-left of a
 * 1200px field, which is what the operator was looking at on Dashboard, the *first* nav item and
 * the landing of the morning read.
 *
 * Nine of the fourteen destinations render through this one component, so composing it here fixes
 * nine screens with one change and no duplication. What it adds is the thing a bare sentence
 * cannot carry:
 *
 * - **A milestone chip**, so *when* is answerable at a glance instead of being buried mid-sentence.
 *   ⚠ **Structured rather than parsed out of the prose.** Regexing `M2` from a sentence would work
 *   until someone rewrote the sentence, and then the chip would silently vanish or, worse, report
 *   the wrong milestone. It is a second map keyed the same way, and a screen with no known
 *   milestone renders no chip rather than a guess.
 * - **A drawn mark**, from the same 1.5px icon family as the nav, so the screen reads as composed
 *   rather than as text that failed to load.
 * - **The measure capped and the block centred in the field**, which is what makes an empty screen
 *   look deliberate. The old one was left-aligned prose against 960px of nothing.
 *
 * ⚠ **It stays quiet on purpose.** These screens are *not* where the work is, and a placeholder
 * that competes with a real screen for attention teaches the operator to distrust the difference.
 * No accent, no fill, one tertiary mark.
 */

import type { JSX } from 'react';

import { Icon } from '../design/components/icon/Icon.jsx';

/**
 * The milestone each screen is waiting on, where the copy names one.
 *
 * Keyed by nav id like `PLACEHOLDER_COPY`, and deliberately incomplete: `learn`, `wallet` and
 * `settings` are blocked on decisions rather than milestones, so they get no chip. Inventing one
 * would be putting a date on a thing nobody has scheduled.
 */
export const PLACEHOLDER_MILESTONE: Record<string, string> = {
  dashboard: 'M2',
  strategies: 'M1',
  fleet: 'M3',
  orders: 'M2',
  research: 'M1',
  markets: 'Partly built',
  intel: 'M2',
  books: 'M3',
  portfolio: 'M2',
  reports: 'M2',
  system: 'Partly built',
};

/** The mark each screen shows, from the nav's own icon set so the two agree. */
const PLACEHOLDER_ICON: Record<string, string> = {
  dashboard: 'gauge',
  strategies: 'list',
  fleet: 'layers',
  orders: 'receipt',
  research: 'flask-conical',
  markets: 'candlestick-chart',
  intel: 'radio',
  books: 'book-open',
  portfolio: 'pie-chart',
  wallet: 'wallet',
  reports: 'file-text',
  learn: 'graduation-cap',
  system: 'server',
  settings: 'settings',
};

export function Placeholder({
  children,
  screen,
  title,
}: {
  children: string;
  /** Nav id, for the mark and the milestone. Omitted only by callers that have neither. */
  screen?: string;
  title?: string;
}): JSX.Element {
  const milestone = screen ? PLACEHOLDER_MILESTONE[screen] : undefined;
  const icon = (screen ? PLACEHOLDER_ICON[screen] : undefined) ?? 'circle-dashed';

  return (
    <div className="jmt-screen">
      <div className="jmt-blank">
        <span aria-hidden="true" className="jmt-blank__mark">
          <Icon name={icon} size={22} />
        </span>
        <h2 className="jmt-blank__title">{title ?? 'Not built yet'}</h2>
        <p className="jmt-blank__body">{children}</p>
        {milestone === undefined ? null : (
          <p className="jmt-blank__when">
            <span className="jmt-blank__chip">{milestone}</span>
            <span>is when this screen gets its data</span>
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * One sentence per screen, keyed by nav id, each naming what has to exist first.
 *
 * Keyed rather than one component per file: fourteen files that each render one string is fourteen
 * files to delete, and the deletion is the point — a screen leaves this map when it becomes real.
 */
export const PLACEHOLDER_COPY: Record<string, string> = {
  dashboard:
    'Nothing to summarise yet. The dashboard reports live strategies, open orders and ' +
    'today’s P&L, and none of those exist until the engine runs in M2.',
  strategies:
    'No strategies. Authoring and the strategy runtime are M1 — backlog tasks 34 onward — ' +
    'and there is no table behind this screen yet.',
  fleet:
    'No candidates. The fleet is 10–20 strategies running concurrently, which is M3 work; ' +
    'until the backtester exists there is nothing to promote into it.',
  orders:
    'No orders. The execution engine is M2, and nothing in this system can place one ' +
    'before then — strategy code cannot reach an exchange call at all (H1).',
  research:
    'No backtests. The backtester is M1, backlog task 64, and it needs the strategy SDK ' +
    'and the sandbox before it can run anything.',
  // The chart above is built (task 31). This copy described the screen as *waiting* for it and
  // went on printing that underneath a working chart — caught by looking at the rendered screen,
  // which is the whole argument for `ops/screenshot_ui.py`. What is still missing is the rest of
  // S6, so that is what it now says.
  markets:
    'The screener, correlation matrix and market-regime verdict are not built. Each reads an ' +
    'endpoint that does not exist yet — /screener, /correlations, /regime — so this screen has ' +
    'no hero: the spec gives that slot to the regime verdict, and a last price is not a verdict.',
  intel:
    'No sources shown. The intel tap runs as a scheduled job and writes to the vault, not ' +
    'to this database; wiring it to a screen is M2.',
  books:
    'No books. Capital allocation across the core, satellite and venture books is M3, and ' +
    'the venture cap it exists to enforce has nothing to cap yet.',
  portfolio:
    'No positions. Positions come from the execution engine in M2; the exchange-account ' +
    'balance endpoints are not built either — OKXConnector.fetch_balances still raises.',
  wallet:
    'No wallet. On-chain custody is deferred until spot has run stably, and the exchange ' +
    'credentials that do exist are managed over the API rather than from a screen.',
  reports:
    'No reports. Performance reporting needs trades to report on, which needs the engine.',
  learn:
    'The knowledge base is written in the vault and not yet served from here. It becomes a ' +
    'screen when the Coach can link into it.',
  system:
    'System health is partly live below — the rest of this screen reports engine components, ' +
    'queue depths and the log viewer, none of which exist before M2.',
  settings:
    'No settings. Everything configurable today is a YAML file read at service start, and ' +
    'exposing it to a form means deciding what a live change is allowed to do mid-trade.',
};
